import type {PolymorphicComponent} from '@github-ui/react-polymorphic'
import {type Config as DOMPurifyConfig, sanitize, type SanitizedHTML} from '@github-ui/sanitize'
import type {TextProps} from '@primer/react'
import {Text} from '@primer/react'
import type React from 'react'

interface UnsafeHTMLProps {
  /**
   * Set the rendered HTML of the component. This HTML will always be sanitized by DOMPurify
   * before being rendered to prevent XSS attacks.
   */
  html: string
  /**
   * Optional config passed to DOMPurify when sanitizing HTML.
   */
  domPurifyConfig?: DOMPurifyConfig
}

function getSanitizedHTMLAndProps<T>(propsWithHtml: T & UnsafeHTMLProps): {sanitizedHTML: SanitizedHTML; props: T} {
  const {html, domPurifyConfig, ...props} = propsWithHtml
  const config = {
    ...domPurifyConfig,
    // ensure we return a string, not a DOM node
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  } satisfies DOMPurifyConfig

  // No `RETURN_TRUSTED_TYPE`: sanitize()'s default path already returns a
  // `TrustedHTML` (policy active) or `SafeHTMLString` (flag off / SSR / no TT).
  return {
    sanitizedHTML: sanitize(html, config),
    props: props as unknown as T,
  }
}

/**
 * `UnsafeHTMLText` extends `Text` from `@primer/react` with props for rendering HTML strings
 * that need to be sanitized. The HTML will always be run through DOMPurify before being rendered
 * to prevent XSS attacks.
 */
export const UnsafeHTMLText = withSanitizedHTML<TextProps>(Text) as PolymorphicComponent<
  'div' | 'span' | 'p' | 'strong' | 'em' | 'pre' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'a',
  TextProps<'div' | 'span' | 'p' | 'strong' | 'em' | 'pre' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'a'> &
    UnsafeHTMLProps
>

type DivProps = UnsafeHTMLProps & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>

const Div = ({ref, ...props}: DivProps & {ref?: React.Ref<HTMLDivElement>}) => <div {...props} ref={ref} />
Div.displayName = 'Div'

/**
 * `UnsafeHTMLDiv` extends `div` with props for rendering HTML strings
 * that need to be sanitized. The HTML will always be run through DOMPurify before being rendered
 * to prevent XSS attacks.
 */
export const UnsafeHTMLDiv = withSanitizedHTML<DivProps>(Div)

/**
 * A higher-order component that extends a basic component by offering
 * an `html` prop that always sanitizes its content via DOMPurify.
 */
function withSanitizedHTML<Props extends object>(Component: React.ComponentType<Props>) {
  const UnsafeHTMLComponent = (propsWithHtml: Props & UnsafeHTMLProps) => {
    const {sanitizedHTML, props} = getSanitizedHTMLAndProps(propsWithHtml)
    // Forward `sanitizedHTML` as-is: coercing a `TrustedHTML` to a string breaks Trusted Types under enforced CSP.
    return (
      <Component
        {...props}
        // `suppressHydrationWarning`: on the server `__html` is a string, on the client it's `TrustedHTML`, but both render identical DOM output
        suppressHydrationWarning
        // eslint-disable-next-line react/forbid-component-props -- safe-html is the only place in the codebase where `dangerouslySetInnerHTML` should be allowed
        dangerouslySetInnerHTML={{__html: sanitizedHTML}}
      />
    )
  }
  UnsafeHTMLComponent.displayName = `UnsafeHTML${Component.displayName || Component.name}`

  return UnsafeHTMLComponent
}
